import { useMemo, useState } from 'react';
import { App, Form } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import type { ComponentTwinsGroupResponseDto } from '@evimo/types';
import { usePermission } from '~/app/auth/hooks/usePermission';
import type { AssetDetailComponent } from '~/app/services/asset/asset.service';
import { ComponentTwinsGroupService } from '~/app/services/component-twins-group/component-twins-group.service';
import { toComponentOptions, type TwinsGroupFormValues } from '../types';

export function useComponentTwinsGroupsSection(
  organizationId: string,
  assetId: string,
  components: AssetDetailComponent[],
) {
  const { t } = useTranslation('assets');
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const [form] = Form.useForm<TwinsGroupFormValues>();
  const [editingGroup, setEditingGroup] =
    useState<ComponentTwinsGroupResponseDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const canRead = hasPermission(PermissionKey.COMPONENT_TWINS_READ);
  const canCreate = hasPermission(PermissionKey.COMPONENT_TWINS_CREATE);
  const canUpdate = hasPermission(PermissionKey.COMPONENT_TWINS_UPDATE);
  const canDelete = hasPermission(PermissionKey.COMPONENT_TWINS_DELETE);
  const canMutate = canCreate || canUpdate || canDelete;

  const queryKey = useMemo(
    () => ['component-twins-groups', organizationId, assetId] as const,
    [organizationId, assetId],
  );

  const groupsQuery = useQuery({
    queryKey,
    enabled: Boolean(organizationId) && Boolean(assetId) && canRead,
    queryFn: async () => {
      const response = await ComponentTwinsGroupService.list(
        organizationId,
        assetId,
      );
      return response.data;
    },
  });

  const componentOptions = useMemo(
    () => toComponentOptions(components),
    [components],
  );

  const componentsById = useMemo(
    () => new Map(components.map((component) => [component.id, component])),
    [components],
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey });
  };

  const createMutation = useMutation({
    mutationFn: (values: TwinsGroupFormValues) =>
      ComponentTwinsGroupService.create(organizationId, assetId, values),
    onSuccess: async () => {
      message.success(
        t('detail.twins.createSuccess', 'Twins group created successfully'),
      );
      setModalOpen(false);
      form.resetFields();
      await invalidate();
    },
    onError: (error: Error) => {
      message.error(
        error.message ||
          t('detail.twins.createError', 'Failed to create twins group'),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: TwinsGroupFormValues) => {
      if (!editingGroup) {
        throw new Error('Missing group');
      }
      return ComponentTwinsGroupService.update(
        organizationId,
        assetId,
        editingGroup.id,
        values,
      );
    },
    onSuccess: async () => {
      message.success(
        t('detail.twins.updateSuccess', 'Twins group updated successfully'),
      );
      setModalOpen(false);
      setEditingGroup(null);
      form.resetFields();
      await invalidate();
    },
    onError: (error: Error) => {
      message.error(
        error.message ||
          t('detail.twins.updateError', 'Failed to update twins group'),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (groupId: string) =>
      ComponentTwinsGroupService.delete(organizationId, assetId, groupId),
    onSuccess: async () => {
      message.success(
        t('detail.twins.deleteSuccess', 'Twins group deleted successfully'),
      );
      await invalidate();
    },
    onError: (error: Error) => {
      message.error(
        error.message ||
          t('detail.twins.deleteError', 'Failed to delete twins group'),
      );
    },
  });

  const openCreateModal = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (group: ComponentTwinsGroupResponseDto) => {
    setEditingGroup(group);
    form.setFieldsValue({
      groupName: group.groupName,
      componentTwinsIds: group.componentTwinsIds,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingGroup(null);
    form.resetFields();
  };

  const submitModal = async () => {
    const values = await form.validateFields();
    if (editingGroup) {
      await updateMutation.mutateAsync(values);
      return;
    }
    await createMutation.mutateAsync(values);
  };

  const confirmDelete = (group: ComponentTwinsGroupResponseDto) => {
    modal.confirm({
      title: t('detail.twins.deleteConfirmTitle', 'Delete twins group?'),
      content: t(
        'detail.twins.deleteConfirmContent',
        'This removes the group "{{name}}" but does not delete the components.',
        { name: group.groupName },
      ),
      okType: 'danger',
      onOk: () => deleteMutation.mutateAsync(group.id),
    });
  };

  return {
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canMutate,
    groups: groupsQuery.data ?? [],
    isLoading: groupsQuery.isLoading,
    isSaving: createMutation.isPending || updateMutation.isPending,
    form,
    modalOpen,
    editingGroup,
    componentOptions,
    componentsById,
    openCreateModal,
    openEditModal,
    closeModal,
    submitModal,
    confirmDelete,
  };
}
